const BALANCE_VISIBILITY_STORAGE_KEY = 'balance_visibility_enabled'
const THEME_PREFERENCE_STORAGE_KEY = 'theme_preference'
const LEGACY_DARK_STORAGE_KEY = 'dark_theme_enabled'

export const THEME_PREFERENCES = Object.freeze({
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system',
})

export const THEME_CYCLE = Object.freeze([
  THEME_PREFERENCES.SYSTEM,
  THEME_PREFERENCES.LIGHT,
  THEME_PREFERENCES.DARK,
])

const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)'

const getMediaQueryList = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return null
  }
  return window.matchMedia(DARK_MEDIA_QUERY)
}

const systemPrefersDark = () => {
  const mql = getMediaQueryList()
  return mql ? mql.matches : false
}

const readThemePreference = () => {
  const stored = window.localStorage.getItem(THEME_PREFERENCE_STORAGE_KEY)
  if (THEME_CYCLE.includes(stored)) return stored

  const legacy = window.localStorage.getItem(LEGACY_DARK_STORAGE_KEY)
  if (legacy !== null) {
    const migrated =
      legacy === 'true' ? THEME_PREFERENCES.DARK : THEME_PREFERENCES.LIGHT
    window.localStorage.setItem(THEME_PREFERENCE_STORAGE_KEY, migrated)
    window.localStorage.removeItem(LEGACY_DARK_STORAGE_KEY)
    return migrated
  }

  return THEME_PREFERENCES.SYSTEM
}

const resolveDark = (preference) => {
  if (preference === THEME_PREFERENCES.DARK) return true
  if (preference === THEME_PREFERENCES.LIGHT) return false
  return systemPrefersDark()
}

const applyThemeClass = (dark) => {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  root.classList.toggle('theme-dark', dark)
  root.classList.toggle('theme-light', !dark)
}

const initialThemePreference = readThemePreference()

export default {
  namespaced: true,
  state: {
    sbState: false,
    sbNavState: false,
    themePreference: initialThemePreference,
    dark: resolveDark(initialThemePreference),
    balanceVisible:
      window.localStorage.getItem(BALANCE_VISIBILITY_STORAGE_KEY) !== 'false',
    favoriteBrands: [],
    centToken: null,
    kycToast: null,
    paymentToast: null,
  },
  mutations: {
    toggleSidebarState(state) {
      state.sbState = !state.sbState;
    },
    toggleSidebarNavState(state) {
      state.sbNavState = !state.sbNavState;
    },
    setThemePreference(state, preference) {
      const next = THEME_CYCLE.includes(preference)
        ? preference
        : THEME_PREFERENCES.SYSTEM
      state.themePreference = next
      window.localStorage.setItem(THEME_PREFERENCE_STORAGE_KEY, next)
      state.dark = resolveDark(next)
      applyThemeClass(state.dark)
    },
    applySystemTheme(state) {
      if (state.themePreference !== THEME_PREFERENCES.SYSTEM) return
      state.dark = systemPrefersDark()
      applyThemeClass(state.dark)
    },
    toggleBalanceVisibility(state) {
      state.balanceVisible = !state.balanceVisible
      window.localStorage.setItem(
        BALANCE_VISIBILITY_STORAGE_KEY,
        state.balanceVisible
      )
    },
    setCentrifugeToken(state, token) {
      state.centToken = token;
    },
    setSubMenu(state, status) {
      state.sbState = status;
    },
    setKycToast(state, status) {
      state.kycToast = status
    },
    setPaymentToast(state, status) {
      state.paymentToast = status
    }
  },
  actions: {
    toggleSbState({ commit }) {
      commit('toggleSidebarState');
    },
    toggleSbNavState({ commit }) {
      commit('toggleSidebarNavState');
    },
    setTheme({ commit }, preference) {
      commit('setThemePreference', preference);
    },
    cycleTheme({ state, commit }) {
      const index = THEME_CYCLE.indexOf(state.themePreference)
      commit('setThemePreference', THEME_CYCLE[(index + 1) % THEME_CYCLE.length]);
    },
    toggleTheme({ state, commit }) {
      commit(
        'setThemePreference',
        state.dark ? THEME_PREFERENCES.LIGHT : THEME_PREFERENCES.DARK
      );
    },
    initTheme({ state, commit }) {
      applyThemeClass(state.dark)

      const mql = getMediaQueryList()
      if (!mql) return

      const onChange = () => commit('applySystemTheme')
      if (typeof mql.addEventListener === 'function') {
        mql.addEventListener('change', onChange)
      } else if (typeof mql.addListener === 'function') {
        mql.addListener(onChange) 
      }
    },
    toggleBalanceVisibility({ commit }) {
      commit('toggleBalanceVisibility')
    },
    setCentToken({ commit }, token) {
      commit('setCentrifugeToken', token);
    },
    hideSubMenu({ commit }) {
      commit('setSubMenu', false);
    },
    showKycToast({ commit }) {
      commit('setKycToast', true)
    },
    showPaymentToast({ commit }) {
        commit('setPaymentToast', true)
    },
    hideKycToast({ commit }) {
      commit('setKycToast', false)
    },
    hidePaymentToast({ commit }) {
        commit('setPaymentToast', false)
    }
  },
};
